/**
 * x-dialog directive.
 *
 * Manages a `<dialog>` element with open/close state, GSAP animations, focus
 * restoration, scroll locking, and light-dismiss behaviour. Only one dialog
 * may be open at a time; opening a new one automatically closes any others.
 *
 * Sub-directives:
 *   x-dialog:element - Applied to the `<dialog>` element itself. Binds the
 *                      GSAP open/close animation via `x-effect`.
 *   x-dialog:toggle  - Applied to a trigger button. Dispatches `dialog-toggle`
 *                      unless the native `commandfor` API is already handling it.
 *
 * @options (root only)
 *   open            - Whether to open the dialog on mount (default: false).
 *   openDuration    - GSAP open animation duration in seconds (default: 0.2).
 *   closeDuration   - GSAP close animation duration in seconds (default: 0.2).
 *   modalBreakpoint - Viewport width below which `dialog.show()` is used instead
 *                     of `dialog.showModal()`. Set to `0` to always use modal,
 *                     `false` to always use non-modal (default: 640).
 *
 * @options (element only)
 *   animation - Override the animation key. Available values: "fromRight",
 *               "fromCenter". Auto-selected based on `useModal` when omitted.
 *
 * @events dispatched
 *   dialog-opening  - Fired when the dialog begins opening.
 *   dialog-opened   - Fired when the open animation completes.
 *   dialog-closing  - Fired when the dialog begins closing.
 *   dialog-closed   - Fired when the close animation completes.
 *
 * @example
 *   <div id="my-dialog" x-dialog="{ open: false, modalBreakpoint: 640 }">
 *     <dialog x-dialog:element>…</dialog>
 *   </div>
 *   <button x-dialog:toggle commandfor="my-dialog">Open</button>
 */
(() => {
  /** @type {Map<Element, object>} Registry of all active dialog instances keyed by root element. */
  const Instances = new Map();

  /**
   * Closes all opened dialogs in the provided collection, or every known
   * instance when no collection is supplied.
   *
   * @param {object[] | null} collection - Array of dialog data instances, or
   *   null to target all registered instances.
   * @returns {Promise<void>} Resolves once all targeted dialogs have closed.
   */
  const closeAll = (collection = null) => {
    return new Promise((resolve) => {
      let instances = collection;

      if (null === collection) {
        instances = Array.from(Instances.entries()).map(
          (instance) => instance[1],
        );
      }

      const opened = instances.filter(
        (instance) => true === instance.__dialogOpened,
      );

      if (!opened.length) {
        resolve();
      } else {
        Promise.allSettled(
          opened.map((instance) => instance.closeDialog()),
        ).then(resolve);
      }
    });
  };

  const root = (
    Alpine,
    el,
    {
      open = false,
      openDuration = 0.2,
      closeDuration = 0.2,
      modalBreakpoint = 640,
    },
  ) => {
    let delayedClose, delayedOpen;

    Alpine.bind(el, () => ({
      /** Handles the global `dialog-toggle` window event, filtered by element ID. */
      ["@dialog-toggle.window"](event) {
        if (!event.detail.id || event.detail.id !== this.$root.id) {
          return;
        }

        this.toggleDialog();
      },

      /** Closes the dialog when a `dialog-close` event is received. */
      ["@dialog-close"]() {
        this.closeDialog();
      },

      /** Opens the dialog when a `dialog-show` event is received. */
      ["@dialog-show"]() {
        this.showDialog();
      },

      /**
       * Handles Invoker API `command` events, mapping `--toggle-dialog`,
       * `--open-dialog`, and `--close-dialog` to their respective methods.
       */
      ["@command"]($event) {
        const command = $event?.command;

        if (!command) return;

        if ("--toggle-dialog" === command) {
          this.toggleDialog();
        } else if ("--open-dialog" === command) {
          this.showDialog();
        } else if ("--close-dialog" === command) {
          this.closeDialog();
        }
      },

      /** Closes the dialog on a pointer-down event outside the dialog element. */
      ["@pointerdown"]($event) {
        const element = this.$refs.dialog;

        if (!element.open) return;

        if (this.$app.$isClickedOutside($event, element)) {
          this.closeDialog();
        }
      },

      /** Closes the dialog when the Escape key is pressed globally. */
      ["@keydown.escape.window"]() {
        if (this.__dialogOpened) {
          this.closeDialog();
        }
      },

      /** Recalculates modal mode on window resize. */
      ["@resize.window.debounce"]() {
        this.handleResize();
      },

      /** Recalculates modal mode on orientation change. */
      ["@orientationchange.window"]() {
        this.handleResize();
      },

      ["x-data"]() {
        let previousScrollY = 0;
        let previousActiveElement = null;

        return {
          useModal:
            false !== modalBreakpoint
              ? window.innerWidth > modalBreakpoint
              : false,
          __dialogIsClosing: false,
          __dialogIsOpening: false,
          __dialogOpened: false,
          __dialogOpenDuration: openDuration,
          __dialogCloseDuration: closeDuration,

          /**
           * Registers this instance in the global map and opens the dialog
           * on mount when the `open` option is true.
           */
          init() {
            Instances.set(this.$el, this);

            this.handleResize();

            if (open) {
              this.$nextTick(() => {
                this.showDialog();
              });
            }
          },

          /**
           * Removes this instance from the global registry on teardown.
           */
          destroy() {
            Instances.delete(this.$el);
          },

          /**
           * Updates `useModal` based on the current viewport width.
           * No-ops when `modalBreakpoint` is falsy.
           */
          handleResize() {
            if (!modalBreakpoint) {
              return;
            }

            this.useModal = window.innerWidth > modalBreakpoint;
          },

          /**
           * Opens the dialog, closing all other open dialogs first.
           * Hides the scrollbar, sets the non-modal top offset, and runs the
           * open animation. Resolves when the animation completes.
           * @returns {Promise<void>}
           */
          showDialog() {
            return new Promise(async (resolve) => {
              const { dialog } = this.$refs;

              if (this.__dialogOpened || this.__dialogIsOpening) {
                return resolve();
              }

              const opened = Array.from(Instances.entries())
                .filter((entry) => {
                  return entry[0].id !== this.$root.id;
                })
                .map((entry) => entry[1]);

              await closeAll(opened);

              previousActiveElement = document.activeElement;

              if (delayedClose) {
                delayedClose.kill();
              }

              const scrollY = window.scrollY;

              previousScrollY = scrollY;

              const { top, height } =
                window["siteHeader"].getBoundingClientRect();

              this.$root.style.setProperty(
                "--non-modal-top-offset",
                this.$app.$toRem(top + height),
              );

              requestAnimationFrame(() => {
                this.$scrollbar.hide();

                if (this.useModal) {
                  dialog.showModal();
                } else {
                  dialog.show();
                }

                this.__dialogIsOpening = true;

                this.$dispatch("dialog-opening", { instance: this });

                delayedOpen = this.$gsap.delayedCall(
                  this.__dialogOpenDuration,
                  () => {
                    this.__dialogIsOpening = false;
                    this.__dialogOpened = true;

                    this.$dispatch(`dialog-opened`, { instance: this });

                    delayedOpen = undefined;

                    resolve();
                  },
                );
              });
            });
          },

          /**
           * Closes the dialog and runs the close animation. Restores the
           * scroll position, resets the scrollbar, and returns focus to the
           * previously active element when done. Resolves when the animation
           * completes.
           * @returns {Promise<void>}
           */
          closeDialog() {
            return new Promise((resolve) => {
              const { dialog } = this.$refs;

              if (!this.__dialogOpened || this.__dialogIsClosing) {
                return resolve();
              }

              if (delayedOpen) {
                delayedOpen.kill();
              }

              this.__dialogIsClosing = true;

              this.$dispatch("dialog-closing", { instance: this });

              delayedClose = this.$gsap.delayedCall(
                this.__dialogCloseDuration,
                () => {
                  this.__dialogIsClosing = false;
                  this.__dialogOpened = false;

                  window.scrollTo({
                    top: previousScrollY,
                    behavior: "instant",
                  });

                  dialog.close();

                  this.$scrollbar.reset();

                  this.$dispatch("dialog-closed", { instance: this });

                  if (previousActiveElement) {
                    previousActiveElement.focus();
                    previousActiveElement = null;
                  }

                  delayedClose = undefined;

                  resolve();
                },
              );
            });
          },

          /**
           * Toggles the dialog open or closed based on its current state.
           */
          toggleDialog() {
            if (this.__dialogOpened) {
              this.closeDialog();
            } else {
              this.showDialog();
            }
          },

          /**
           * Returns whether the dialog is currently open.
           * @returns {boolean} True when the dialog is open.
           */
          get isOpened() {
            return this.__dialogOpened;
          },
        };
      },
    }));
  };

  /**
   * GSAP animation presets for opening and closing the dialog element.
   * `fromRight` slides in from the right edge (used for modal/drawer mode).
   * `fromCenter` scales and fades in from the centre (used for non-modal mode).
   */
  const AnimationProps = {
    fromRight: {
      opening: {
        from: {
          x: "100%",
          scale: 1,
          opacity: 1,
        },
        to: {
          x: 0,
        },
      },
      closing: {
        from: {
          x: 0,
          scale: 1,
          opacity: 1,
        },
        to: {
          x: "100%",
        },
      },
    },
    fromCenter: {
      opening: {
        from: {
          x: 0,
          scale: 0.9,
          opacity: 0,
          transformOrigin: "top center",
        },
        to: {
          x: 0,
          scale: 1,
          opacity: 1,
        },
      },
      closing: {
        from: {
          x: 0,
          scale: 1,
          opacity: 1,
          transformOrigin: "top center",
        },
        to: {
          x: 0,
          scale: 0.9,
          opacity: 0,
        },
      },
    },
  };

  /**
   * Binds the `<dialog>` element with an `x-effect` that runs the appropriate
   * GSAP animation whenever the opening or closing state flags change.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The `<dialog>` element.
   * @param {{ animation?: string|null }} options - Optional animation key override.
   */
  const element = (Alpine, el, { animation = null }) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "dialog",
      [":tabindex"]: "-1",

      ["@keydown.escape.prevent"]() {
        this.closeDialog();
      },

      ["x-effect"]() {
        if (true === this.__dialogIsOpening) {
          this.$gsap.killTweensOf(this.$el);

          let key = animation;

          if (!animation) {
            key = this.useModal ? "fromRight" : "fromCenter";
          }

          const { from, to } = AnimationProps[key].opening;

          this.$gsap.fromTo(this.$el, from, {
            ...to,
            duration: this.__dialogOpenDuration,
          });
        } else if (true === this.__dialogIsClosing) {
          this.$gsap.killTweensOf(this.$el);

          let key = animation;

          if (!animation) {
            key = this.useModal ? "fromRight" : "fromCenter";
          }

          const { from, to } = AnimationProps[key].closing;

          this.$gsap.fromTo(this.$el, from, {
            ...to,
            duration: this.__dialogCloseDuration,
          });
        }
      },
    }));
  };

  /**
   * Binds a toggle button to dispatch the `dialog-toggle` window event.
   * Defers to the native Invoker API (`commandForElement`) when available.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The toggle trigger element.
   */
  const toggle = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["@click"]($event) {
        if (this.$el.commandForElement) return;

        $event.preventDefault();

        this.$dispatch("dialog-toggle", {
          id: el.getAttribute("commandfor"),
          toggle: this,
        });
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("dialog", (el, { value, expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};

      if ("element" === value) {
        element(Alpine, el, options);
      } else if ("toggle" === value) {
        toggle(Alpine, el, options);
      } else {
        root(Alpine, el, options);
      }
    });
  });
})();
